import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useInfiniteQuery } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import { InvalidResponseCode } from 'tg-resources';
import api, { type ResourceParams, useApiFetch } from '../Services/Api';
import { startLoading, finishLoading } from '../Redux/LoadingActions';
import { setSagaMessage, pushError } from '../Redux/ApplicationActions';
import { receiveCaseNotes, getDonor } from '../Redux/DonorActions';
import { convertToCaseNote } from './caseNotes';
import type {
  CaseView,
  DonorOption,
  WorkflowOption,
  DomoEmbedToken,
  DomoInsightsEmbedToken,
  RecipientUploadRecord,
  RecipientOption,
  DomoDashboardParams,
  ProviderService,
  ServiceProvider,
  StaticTask,
  CaseTimelineEvent,
  InsightsEmbedUrl,
  UpdateChatroomManagerParams,
  UpdateCaseAdminParams,
  CaseNote,
  CaseTimeline,
} from './types';
import { transformWorkflowDef, transformFormData, transformApiTaskToStaticTask } from './TaskDetailsUtils';
import { parseRemoteChatroom } from './chatroom';
import queryClient from '../queryClient';
import type {
  ApiCaseNote,
  ApiCaseNotificationPreview,
  ApiCaseNotificationPreviewResponse,
  ApiCaseTimeline,
  ApiCaseTimelineEvent,
  ApiCheckUserExistsResponse,
  ApiDonorImportFieldResponse,
  ApiRecipientUploadRecord,
  ApiSendCaseNotification,
  ApiTag,
  ApiTasks,
  ApiWorkflowAndForms,
} from './apiTypes';
import type { DonorImportField } from './types';
import type { RootState } from '../Redux';
import { convertToCaseTimelineEvent, convertToCaseTimelinePendingField } from './caseTimeline';
import { compareDesc } from 'flowhawk-core';
import { useCanUseUnosApi } from './hasPermissions';
import { receiveAddChatroomManager, receiveRemoveChatroomManager } from '../Redux/ChatListActions';

const FETCH_CHATROOM_STALE_TIME = 5; // 5 minutes
const FETCH_DOMO_STALE_TIME = 1000 * 60 * 60; // 1 hour
const FETCH_DOMO_CACHE_TIME = FETCH_DOMO_STALE_TIME + 5 * 60 * 1000; // STALE_TIME + 5 minutes;

const expireQueryCache = (queryKey: string[]) => {
  queryClient.invalidateQueries({ queryKey });
};

export const useWorkflow = (workflowKey?: string) => {
  const apiFetch = useApiFetch();

  return useQuery<ApiWorkflowAndForms | void>({
    queryKey: ['workflowDefinition', workflowKey],
    queryFn: async () => {
      if (!workflowKey || workflowKey === 'LEGACY') {
        return null;
      }

      const partialResourceParams: ResourceParams = { kwargs: { key: workflowKey }, method: 'fetch' };
      return apiFetch(api.txp.workflowDefinition, partialResourceParams);
    },
  });
};

/**
 * Get a workflow definition, including forms, given the workflow key
 * Provides useQuery response
 *
 * @param {string} workflowKey
 */
export const useWorkflowDefinition = (workflowKey?: string) => {
  const { data, ...queryState } = useWorkflow(workflowKey);
  const transformed = useMemo(() => (data ? transformWorkflowDef(data) : undefined), [data]);
  return {
    ...queryState,
    data: transformed,
  };
};

/**
 * Get the form definitions for a workflow given the workflow key
 * Provides useQuery response
 *
 * @param {string} workflowKey
 */
export const useWorkflowForms = (workflowKey: string) => {
  const defaultFormDefinitions = useSelector((state: RootState) => state.forms.formDef);
  const { data, ...queryState } = useWorkflow(workflowKey);
  const formData = useMemo(
    () => ({ ...defaultFormDefinitions, ...transformFormData(data?.forms ?? []) }),
    [defaultFormDefinitions, data],
  );

  return {
    ...queryState,
    data: formData,
  };
};

/**
 * Provides workflow options as an array of key, name pairs
 * Provides useQuery response
 *
 * @returns {Array<WorkflowOption>}
 */
export const useWorkflowOptions = () => {
  const apiFetch = useApiFetch();

  return useQuery<WorkflowOption[]>({
    queryKey: ['workflowOptions'],
    queryFn: async () => apiFetch(api.txp.workflowOptions),
  });
};

export const useCaseTasks = (caseId: number, workflowKey?: string) => {
  const apiFetch = useApiFetch();
  return useQuery<ApiTasks>({
    queryKey: ['caseTasks', workflowKey, caseId],
    queryFn: async () =>
      apiFetch(api.txp.tasks, { kwargs: { workflow: workflowKey }, query: { donor_id: caseId }, method: 'fetch' }),
  });
};

export const useLoadCaseTasks = (caseId: number, workflowKey?: string) => {
  const { data, ...queryState } = useCaseTasks(caseId, workflowKey);

  const tasks = useMemo<StaticTask[]>(
    () => Object.entries(data?.tasks ?? []).map(([taskId, task]) => transformApiTaskToStaticTask(taskId, task)),
    [data],
  );
  const sectionOrder = useMemo(() => [...new Set(tasks.map((t) => t.details.section))], [tasks]);

  return { ...queryState, tasks, sectionOrder };
};

/**
 * Provides donor options as an array of key, name pairs
 * Provides useQuery response
 *
 * @returns {DonorOption[]}
 */
export const useDonorOptions = (search: string, limit: number = 10) => {
  const apiFetch = useApiFetch();

  return useQuery<DonorOption[]>({
    queryKey: ['donorOptions', search, limit],
    queryFn: async () => apiFetch(api.txp.donorOptions, { query: { search, limit }, method: 'fetch' }),
    // prevent flash of empty results. will need to be changed to the following
    // once we upgrade to react-query v5+
    // placeholderData: (previousData) => previousData,
    keepPreviousData: true,
  });
};

/**
 * Provides a list of tag options to use as case filters
 * @returns {Array<ApiTag>}
 */
export const useTagFilters = () => {
  const apiFetch = useApiFetch();

  return useQuery<ApiTag[]>({
    queryKey: ['tagFilters'],
    queryFn: async () => apiFetch(api.txp.tagFilters),
  });
};

/**
 * Provides saved case views
 */
export const useCaseViews = () => {
  const apiFetch = useApiFetch();

  return useQuery<CaseView[]>({
    queryKey: ['caseViews'],
    queryFn: async () => apiFetch(api.txp.casesViews),
  });
};

/**
 * Provides a paginated list of chat rooms
 */
export const useFetchChatrooms = (
  resultsPerPage: number,
  sortBy: string,
  sortDirection: string,
  filter: string,
  search: string,
  caseId?: number | number[] | undefined | null,
) => {
  const apiFetch = useApiFetch();
  const dispatch = useDispatch();

  const queryResult = useInfiniteQuery({
    queryKey: [
      'chatrooms',
      {
        resultsPerPage,
        sortBy,
        sortDirection,
        filter,
        search,
        caseId,
      },
    ],
    queryFn: async (ctx) => {
      const queryParams = {
        page: ctx.pageParam,
        resultsPerPage,
        sortBy,
        sortDirection,
        filter,
        search,
        caseId,
      };
      if (caseId) {
        queryParams.caseId = caseId;
      }
      const result = await apiFetch(api.txp.chatrooms, {
        // array parameters are not yet supported by tg-resources, so we cast to any
        query: queryParams as unknown as Record<string, any>,
        method: 'fetch',
      });
      const rooms = result.chatrooms.map((room: any) => parseRemoteChatroom(room));
      return {
        chatrooms: rooms,
        totalCount: result.totalCount,
        hasNextPage: result.hasNextPage,
        page: result.page,
      };
    },
    getNextPageParam: (lastGroup) => (lastGroup.hasNextPage ? lastGroup.page + 1 : undefined),
    staleTime: FETCH_CHATROOM_STALE_TIME * 60 * 1000, // converting minutes to milliseconds
  });

  // use effect for watching queries to delay message events eg read until list is refreshed
  useEffect(() => {
    if (queryResult.isFetching) {
      dispatch(startLoading('roomsList'));
    } else {
      dispatch(finishLoading('roomsList'));
    }
  }, [queryResult.isFetching, dispatch]);

  return queryResult;
};

export const useCaseDefaultViewers = (orgId: number) => {
  const apiFetch = useApiFetch();

  return useQuery({
    queryKey: ['caseDefaultViewers', orgId],
    queryFn: async () => {
      if (!orgId) {
        return null;
      }

      return apiFetch(api.txp.getOrgDefaultCaseViewers, { kwargs: { orgId }, method: 'fetch' });
    },
  });
};

export const useDomoEmbedToken = (embedId: string, dashboardId: string) => {
  const apiFetch = useApiFetch();

  // Note: Here we set a cache time of just past our 1-hour stale time. The cache time tells
  // react-query how long to keep the token in memory, after there are no components observing it
  // (so if the DomoDashboard is unmounted). We hold onto the token so that if we revisit the same
  // dashboard, we have the embedToken still available to use.
  const query = useQuery<DomoEmbedToken>({
    queryKey: ['domoEmbedToken', dashboardId, embedId],
    queryFn: async () => {
      const result = await apiFetch(api.txp.domoToken, { query: { embedId, dashboardId }, method: 'fetch' });

      return result;
    },
    enabled: !!dashboardId,
    staleTime: FETCH_DOMO_STALE_TIME,
    cacheTime: FETCH_DOMO_CACHE_TIME,
    // DOMO advises that the embed token be refreshed every hour
    refetchInterval: FETCH_DOMO_STALE_TIME,
    refetchIntervalInBackground: true,
  });

  return query;
};

export const useDomoInsightsEmbedToken = (caseId: number, xmatCaseId: string) => {
  const apiFetch = useApiFetch();

  const query = useQuery<DomoInsightsEmbedToken>({
    queryKey: ['domoInsightsEmbedToken', caseId],
    queryFn: async () => {
      const result = await apiFetch(api.txp.domoInsightsToken, { query: { caseId }, method: 'fetch' });

      return result;
    },
    enabled: !!xmatCaseId,
    staleTime: FETCH_DOMO_STALE_TIME,
    cacheTime: FETCH_DOMO_CACHE_TIME,
    // DOMO advises that the embed token be refreshed every hour
    refetchInterval: FETCH_DOMO_STALE_TIME,
    refetchIntervalInBackground: true,
  });

  return query;
};

export const useAnalyticsDashboards = () => {
  const apiFetch = useApiFetch();

  return useQuery<DomoDashboardParams[]>({
    queryKey: ['analyticsDashboards'],
    queryFn: async () => apiFetch(api.txp.analyticsDashboards),
  });
};

export const refetchChatList = () => {
  expireQueryCache(['chatrooms']);
};

export const setChatroomsCachedData = (cachedRooms: any) => {
  queryClient.setQueriesData(['chatrooms'], cachedRooms);
};

export const refetchRecipientUploadRecords = () => {
  expireQueryCache(['recipientUploadRecords']);
};

export const refetchCaseViews = () => {
  expireQueryCache(['caseViews']);
};

export const useRecipientUploadRecords = (page: number = 1, pageSize: number = 10) => {
  const apiFetch = useApiFetch();

  return useQuery<{ records: Array<RecipientUploadRecord>; totalCount: number }>({
    queryKey: ['recipientUploadRecords', page, pageSize],
    queryFn: async () => {
      const result = await apiFetch(api.txp.recipientUploadRecords, {
        query: {
          page,
          limit: pageSize,
        },
        method: 'fetch',
      });

      return {
        records: result.records.map((record: ApiRecipientUploadRecord) => ({
          id: record.id,
          userId: record.user_id,
          numberOfRecords: record.number_of_records,
          statusInfo: record.status,
          uploadingDate: record.last_modified_date,
        })),
        totalCount: result.totalCount,
      };
    },
  });
};

/**
 * Provides recipient options as an array of key, name pairs
 * Provides useQuery response
 *
 * @returns {RecipientOption[]}
 */
export const useRecipientOptions = (search: string, limit: number = 10) => {
  const apiFetch = useApiFetch();

  return useQuery<RecipientOption[]>({
    queryKey: ['recipientOptions', search, limit],
    queryFn: async () => {
      const result = await apiFetch(api.txp.recipientOptions, { query: { search, limit }, method: 'fetch' });
      return result.recipients;
    },
    // prevent flash of empty results. will need to be changed to the following
    // once we upgrade to react-query v5+
    // placeholderData: (previousData) => previousData,
    keepPreviousData: true,
  });
};

export const useCaseNotificationPreviewMutation = () => {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationKey: ['caseNotification', 'preview'],
    mutationFn: async (payload: ApiCaseNotificationPreview) => {
      const result: ApiCaseNotificationPreviewResponse = await apiFetch(api.txp.caseNotificationPreview, {
        method: 'post',
        data: payload,
      });

      return result;
    },
  });
};

export const useCaseNotificationSendMutation = () => {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationKey: ['caseNotification', 'send'],
    mutationFn: async (payload: ApiSendCaseNotification) => {
      await apiFetch(api.txp.caseNotificationSend, {
        method: 'post',
        data: payload,
      });

      return true;
    },
  });
};

/**
 * Provides provider services for a specific provider
 * Provides useQuery response
 *
 * @param {string} providerId - The ID of the provider organization
 * @returns {Array<{ id: string; name: string }>}
 */
export const useProviderServices = (providerId: string) => {
  const apiFetch = useApiFetch();

  return useQuery<ProviderService[]>({
    queryKey: ['provider-services', providerId],
    queryFn: async () => {
      if (!providerId) {
        return [];
      }

      const result = await apiFetch(api.txp.providerServices, {
        kwargs: { providerId },
        method: 'fetch',
      });

      return result ?? [];
    },
    enabled: !!providerId,
  });
};

/**
 * Provides subscribed services for a specific organization
 * Provides useQuery response
 *
 * @param {string} organizationId - The ID of the organization
 * @returns {Array<{ id: string; name: string }>}
 */
export const useSubscribedServices = (organizationId: string) => {
  const apiFetch = useApiFetch();

  return useQuery<ServiceProvider[]>({
    queryKey: ['subscribed-services', organizationId],
    queryFn: async () => {
      if (!organizationId) {
        return [];
      }

      const result = await apiFetch(api.txp.subscribedServices, {
        kwargs: { organizationId },
        method: 'fetch',
      });

      return result ?? [];
    },
    enabled: !!organizationId,
  });
};

/**
 * Hook to fetch organization type for a given organization ID
 * Provides useQuery response
 *
 * @param {string} organizationId - The organization ID to fetch type for
 * @returns {string} The organization type (e.g., 'Vendor', 'OPO', 'TC', etc.)
 */
export const useOrganizationType = (organizationId: string) => {
  const apiFetch = useApiFetch();

  return useQuery<{ organization_type: string }>({
    queryKey: ['organizationType', organizationId],
    queryFn: async () => {
      const result = await apiFetch(api.txp.organizationType, {
        kwargs: { organizationId },
        method: 'fetch',
      });

      return result;
    },
    enabled: !!organizationId,
  });
};

/**
 * Hook that returns if a user exists in the system.
 *
 *
 * @example
 * const checkUserExists = useCheckUserExists();
 * const exists = await checkUserExists({ email: 'user@example.com' });
 * // or
 * const exists = await checkUserExists({ phone: '+15551234567' });
 */
export const useCheckUserExists = () => {
  const apiFetch = useApiFetch();

  return useCallback(
    async (params: { email?: string; phone?: string }): Promise<boolean> => {
      try {
        const result: ApiCheckUserExistsResponse = await apiFetch(api.txp.checkUserExists, {
          method: 'post',
          data: {
            email: params.email?.toLowerCase(),
            phone: params.phone,
          },
        });
        return result?.exists ?? false;
      } catch {
        // On error, return false (assume user doesn't exist)
        return false;
      }
    },
    [apiFetch],
  );
};

/**
 * Hook to check for user info from an invite
 */
export const useInviteInfo = (inviteFactor: string) => {
  const apiFetch = useApiFetch();
  type InviteInfo = {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  return useQuery<InviteInfo>({
    queryKey: ['invite', inviteFactor],
    queryFn: async () => {
      if (inviteFactor) {
        return await apiFetch(api.txp.inviteInfo, { kwargs: { inviteFactor }, method: 'fetch' });
      } else {
        return {};
      }
    },
    staleTime: Infinity, // Only fetch once, we won't need this to update
  });
};

/**
 * Hook to fetch donor information from UNOS
 */
export const useUnosDonor = (donorId: string | undefined) => {
  const apiFetch = useApiFetch();
  const canUseUnosApi = useCanUseUnosApi();

  return useQuery<Record<string, string | number | null>>({
    queryKey: ['unosDonor', donorId],
    enabled: !!donorId && canUseUnosApi,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => apiFetch(api.txp.unosDonor, { method: 'fetch', kwargs: { donorId } }),
  });
};

/**
 * Hook to get the UNOS connection status
 */
export const useUnosStatus = () => {
  const apiFetch = useApiFetch();
  const canUseUnosApi = useCanUseUnosApi();

  return useQuery<{
    connected: boolean;
    expires_at?: string | null;
  }>({
    queryKey: ['unosStatus'],
    queryFn: async () => apiFetch(api.txp.unosStatus, { method: 'fetch' }),
    enabled: canUseUnosApi,
    cacheTime: Infinity,
  });
};

/**
 * Hook to connect to UNOS
 */
export const useUnosConnect = () => {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const result: { auth_url: string } = await apiFetch(api.txp.unosConnect, { method: 'fetch' });

      // Open popup and handle OAuth flow
      return new Promise((resolve, reject) => {
        const popup = window.open(result.auth_url, 'unos-oauth', 'popup,width=600,height=700');

        if (!popup) {
          reject(new Error('Popup blocked. Please allow popups for this site and try again.'));
          return;
        }

        let settled = false;

        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (event.data?.type !== 'unos-oauth-complete') return;
          settled = true;
          cleanup();
          if (event.data.status === 'success') resolve();
          else reject(new Error('UNOS authorization failed. Please try again.'));
        };

        // Poll for popup closure to handle cases where message event doesn't fire
        const pollClosed = setInterval(() => {
          if (popup.closed) {
            cleanup();
            if (!settled) resolve();
          }
        }, 500);

        const cleanup = () => {
          window.removeEventListener('message', handleMessage);
          clearInterval(pollClosed);
        };

        window.addEventListener('message', handleMessage);
      });
    },
    // Invalidate UNOS status query on success to refresh connection status
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unosStatus'] });
    },
  });
};

/**
 * Hook to disconnect from UNOS
 */
export const useUnosDisconnect = () => {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async () => apiFetch(api.txp.unosDisconnect, { method: 'del' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unosStatus'] });
    },
  });
};

export type UnosUserOptions = {
  centerCode: string | null;
  centerType: string | null;
  centerCodeOptions: string[];
};

export const useUnosUserOptions = () => {
  const apiFetch = useApiFetch();
  const canUseUnosApi = useCanUseUnosApi();

  return useQuery<UnosUserOptions>({
    queryKey: ['unosUserOptions'],
    queryFn: async () => apiFetch(api.txp.unosUserOptions, { method: 'fetch' }),
    enabled: canUseUnosApi,
  });
};

export const useUpdateUnosCenterCodes = () => {
  const apiFetch = useApiFetch();
  return useMutation({
    mutationFn: async (centerCodes: string[]) =>
      apiFetch(api.txp.unosUserOptions, { method: 'put', data: { centerCodes } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unosUserOptions'] });
    },
  });
};

export const useSetUnosCenterCode = () => {
  const apiFetch = useApiFetch();
  return useMutation({
    mutationFn: async (centerCode: string) =>
      apiFetch(api.txp.unosUserCenterCode, { method: 'put', data: { centerCode } }),
  });
};

export const useSetUnosCenterType = () => {
  const apiFetch = useApiFetch();
  return useMutation({
    mutationFn: async (centerType: string) =>
      apiFetch(api.txp.unosUserCenterType, { method: 'put', data: { centerType } }),
  });
};

export const usePdfFields = () => {
  const apiFetch = useApiFetch();

  return useQuery<DonorImportField[]>({
    queryKey: ['pdfFields'],
    queryFn: async () => {
      const result: ApiDonorImportFieldResponse = await apiFetch(api.txp.getPdfFields, { method: 'fetch' });
      return result?.fields ?? [];
    },
    staleTime: Infinity,
  });
};

export const useCaseTimeline = (caseId: number) => {
  const apiFetch = useApiFetch();

  return useQuery<CaseTimeline>({
    queryKey: ['caseTimeline', caseId],
    queryFn: async () => {
      const result: ApiCaseTimeline = await apiFetch(api.txp.caseTimeline, {
        kwargs: { caseId },
        method: 'fetch',
      });

      return {
        timeline: (result.timeline ?? [])
          .map((item) => convertToCaseTimelineEvent(item))
          .sort((a, b) => compareDesc(a.timestamp, b.timestamp)),
        pending: (result.pending ?? []).map((item) => convertToCaseTimelinePendingField(item)),
      };
    },
  });
};

// Mutation to add a chat manager to a room
export const useAddChatroomManager = () => {
  const apiFetch = useApiFetch();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (payload: UpdateChatroomManagerParams) => {
      const result = await apiFetch(api.txp.updateChatroomManager, {
        method: 'put',
        kwargs: {
          chatId: payload.chatId,
          userId: payload.userId,
        },
      });

      return result.user_id && typeof result.user_id === 'number' ? result.user_id : undefined;
    },
    onSuccess: (data, variables) => {
      if (data) {
        dispatch(receiveAddChatroomManager(variables.chatId, data));
      }
    },
  });
};

// Mutation to remove a chat manager to a room
export const useRemoveChatroomManager = () => {
  const apiFetch = useApiFetch();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (payload: UpdateChatroomManagerParams) => {
      const result = await apiFetch(api.txp.updateChatroomManager, {
        method: 'del',
        kwargs: {
          chatId: payload.chatId,
          userId: payload.userId,
        },
      });

      return result.user_id && typeof result.user_id === 'number' ? result.user_id : undefined;
    },
    onSuccess: (data, variables) => {
      dispatch(receiveRemoveChatroomManager(variables.chatId, data));
    },
  });
};

/**
 * Provides a single chatroom's info
 */
export const useChatroom = (chatId: number | undefined) => {
  const apiFetch = useApiFetch();

  return useQuery({
    queryKey: ['chat', chatId],
    queryFn: async () =>
      apiFetch(api.txp.chatroom, { method: 'fetch', kwargs: { chatId } }).then((chatroom) => ({
        id: chatroom.chatroom_id,
        name: chatroom.chatroom_name,
        description: chatroom.chatroom_description,
        chatroomType: chatroom.chatroom_type,
        managed: chatroom.managed || false,
        creatorId: chatroom.creator_id,
        memberCount: chatroom.member_count,
        muted: chatroom.do_not_disturb,
        pinNumber: chatroom.pin_number,
        totalMessages: chatroom.total_message_count || 0,
        sentCount: chatroom.sent_count || 0,
        readCount: chatroom.read_count || 0,
        clearedCount: chatroom.cleared_count || 0,
        lastMessage: chatroom.last_instant_message_time
          ? {
              textContent: chatroom.last_instant_message_content
                ? chatroom.last_instant_message_content
                : 'File uploaded',
              sentTime: chatroom.last_instant_message_time,
            }
          : undefined,
        lastUpdateTime: chatroom.last_update_time,
        lastActiveTime: chatroom.last_active_time,
        donorId: chatroom.donor_id,
        organId: chatroom.organ_id,
        followerId: chatroom.follower_id,
        organType: chatroom.organ_type,
        targetOrgId: chatroom.transplant_center,
        createDate: chatroom.create_date,
        donorClosed: chatroom.donor_closed,
        caseName: chatroom.case_name,
        followerName: chatroom.follower_name,
        managerIds: chatroom.manager_ids,
      })),
    enabled: chatId ? true : false,
  });
};

// Mutation to add a chat manager to a room
export const useRemoveCaseAdmin = () => {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async (payload: UpdateCaseAdminParams) => {
      const response = await apiFetch(api.txp.caseAdmin, {
        method: 'del',
        kwargs: {
          caseId: payload.caseId,
          userId: payload.userId,
        },
      });

      return response?.user_id || undefined;
    },
  });
};

/** The note mutations all respond with the case's full note list. */
const toCaseNotes = (result: ApiCaseNote[]): CaseNote[] => result.map(convertToCaseNote);

/**
 * Surfaces case-note edit/delete failures via the redux toast, matching the prior saga:
 * 403 => permission message; 404 => the note is already gone, so refetch the donor.
 */
const handleModifyCaseNoteError = (
  dispatch: ReturnType<typeof useDispatch>,
  caseId: number,
  error: unknown,
  verb: 'edit' | 'delete',
) => {
  if (error instanceof InvalidResponseCode && error.statusCode === 403) {
    dispatch(setSagaMessage('', `You don't have permission to ${verb} this note.`, ''));
  } else if (error instanceof InvalidResponseCode && error.statusCode === 404) {
    dispatch(setSagaMessage('', 'This note is no longer available.', ''));
    dispatch(getDonor(caseId));
  } else {
    dispatch(pushError(`Failed to ${verb} note for case ${caseId}:`, error as Error));
    dispatch(setSagaMessage('', `Failed to ${verb} case note`, ''));
  }
};

export const useCreateCaseNote = (caseId: number) => {
  const apiFetch = useApiFetch();
  const dispatch = useDispatch();

  return useMutation<CaseNote[], unknown, string>({
    mutationFn: async (note: string) =>
      toCaseNotes(await apiFetch(api.txp.createCaseNote, { kwargs: { caseId }, method: 'post', data: { note } })),
    onSuccess: (notes) => dispatch(receiveCaseNotes(caseId, notes)),
    onError: (error) => {
      dispatch(pushError(`Failed to save note for case ${caseId}:`, error as Error));
      dispatch(setSagaMessage('', 'Failed to save case note', ''));
    },
  });
};

export const useEditCaseNote = (caseId: number) => {
  const apiFetch = useApiFetch();
  const dispatch = useDispatch();

  return useMutation<CaseNote[], unknown, { noteId: number; note: string }>({
    mutationFn: async ({ noteId, note }) =>
      toCaseNotes(await apiFetch(api.txp.editCaseNote, { kwargs: { caseId, noteId }, method: 'put', data: { note } })),
    onSuccess: (notes) => dispatch(receiveCaseNotes(caseId, notes)),
    onError: (error) => handleModifyCaseNoteError(dispatch, caseId, error, 'edit'),
  });
};

export const useDeleteCaseNote = (caseId: number) => {
  const apiFetch = useApiFetch();
  const dispatch = useDispatch();

  return useMutation<CaseNote[], unknown, number>({
    mutationFn: async (noteId: number) =>
      toCaseNotes(await apiFetch(api.txp.deleteCaseNote, { kwargs: { caseId, noteId }, method: 'del' })),
    onSuccess: (notes) => dispatch(receiveCaseNotes(caseId, notes)),
    onError: (error) => handleModifyCaseNoteError(dispatch, caseId, error, 'delete'),
  });
};

/**
 * Note: This hook is a bit different, we essentially want to turn off all caching because each embed URL
 * can only be used once (even for the same caseId/deviceSerialNumber). So we essentially make the cached data
 * invalid so tan-stack query will get a new URL on each query. We also are only enabling this query if the
 * Insights embed is visible (controlled by calling parent) so that we do not unnecessarily generate URLs
 *
 * @param caseId
 * @param shouldFetch
 */
export const useInsightsEmbedUrl = (caseId: number, shouldFetch: boolean = false) => {
  const apiFetch = useApiFetch();

  const query = useQuery<InsightsEmbedUrl>({
    queryKey: ['insightsEmbedUrl', caseId],
    queryFn: async () => {
      return await apiFetch(api.txp.caseInsightsEmbed, {
        kwargs: { caseId },
        method: 'fetch',
      });
    },
    enabled: shouldFetch,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 0,
    cacheTime: 0,
  });

  return query;
};
